import React, { useState, useEffect } from 'react';
import { X, Download, FileText, FileSpreadsheet, Calendar, ChevronDown, Check, Search, Columns, Activity } from 'lucide-react';
import { Modal } from "@mui/material";
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';

interface ColumnField {
    field: string;
    label: string;
    type: string;
    default?: boolean;
}

interface ColumnsResponse {
    status: boolean;
    data: {
        report_name?: string;
        [key: string]: any;
    };
}

interface DateFilterValue {
    start: string;
    end: string;
}

interface ExportModalProps {
    isOpen: boolean;
    onClose: () => void;
    templateName: string;
    filters?: Record<string, any>;
    clientId?: string | number;
    asset_type?: string;
}

const AgentExportModal: React.FC<ExportModalProps> = ({ isOpen, onClose, templateName, clientId, asset_type, filters }) => {
    const [columns, setColumns] = useState<ColumnsResponse['data'] | null>(null);
    const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
    const [format, setFormat] = useState<'csv' | 'excel'>('csv');
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [timezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const [columnSearch, setColumnSearch] = useState('');

    // Date filter states
    const [selectedDateColumn, setSelectedDateColumn] = useState<string>('');
    const [dateRangeType, setDateRangeType] = useState<string>('');
    const [customStartDate, setCustomStartDate] = useState<string>('');
    const [customEndDate, setCustomEndDate] = useState<string>('');
    const [isDateColumnOpen, setIsDateColumnOpen] = useState(false);
    const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
    const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
    const [selectedEventStatus, setSelectedEventStatus] = useState<string>('');

    useEffect(() => {
        if (isOpen) {
            fetchColumns();
            setSelectedEventStatus(filters?.event_status || '');
        }
    }, [isOpen, templateName, filters?.event_status]);

    const fetchColumns = async () => {
        setLoading(true);
        try {
            const response = await baseapi.get(`/export/columns/${templateName}`);
            const resData: ColumnsResponse = response.data;

            if (resData.status && resData.data) {
                let formattedColumns: { [key: string]: ColumnField[] } = {};
                let initialSelected: string[] = [];

                // Find the key that ends with '_view' and is an array (e.g., agents_view)
                const viewKey = Object.keys(resData.data).find(key =>
                    key.endsWith('_view') && Array.isArray(resData.data[key])
                );

                if (viewKey) {
                    const fields = resData.data[viewKey] as ColumnField[];
                    formattedColumns = { [viewKey]: fields };
                    initialSelected = fields
                        .filter(f => f.default)
                        .map(f => `${viewKey}.${f.field}`);
                } else if (Array.isArray(resData.data.columns)) {
                    // Fallback to 'columns' key
                    formattedColumns = { [templateName]: resData.data.columns };
                    initialSelected = resData.data.columns
                        .filter(f => f.default)
                        .map(f => `${templateName}.${f.field}`);
                } else {
                    // Original categorised structure
                    Object.entries(resData.data).forEach(([cat, fields]) => {
                        if (Array.isArray(fields) && cat !== 'report_name') {
                            formattedColumns[cat] = fields;
                            fields.forEach(f => {
                                if (f.default) initialSelected.push(`${cat}.${f.field}`);
                            });
                        }
                    });
                }

                setColumns(formattedColumns);
                setSelectedColumns(initialSelected);
            }
        } catch (error: any) {
            console.error('Error fetching columns:', error);
            showError('Failed to fetch export columns');
        } finally {
            setLoading(false);
        }
    };

    const getDateTimeColumns = (): { category: string; field: string; label: string }[] => {
        if (!columns) return [];

        const dateColumns: { category: string; field: string; label: string }[] = [];

        Object.entries(columns).forEach(([category, fields]) => {
            fields.forEach((field: ColumnField) => {
                if (field.type === 'datetime' || field.type === 'date') {
                    dateColumns.push({
                        category,
                        field: field.field,
                        label: `${field.label}`
                    });
                }
            });
        });

        return dateColumns;
    };

    const getDateRange = (): DateFilterValue | null => {
        const now = new Date();
        let start: Date;
        let end: Date = now;

        switch (dateRangeType) {
            case 'last_7_days':
                start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                break;
            case 'last_30_days':
                start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                break;
            case 'custom':
                if (!customStartDate || !customEndDate) return null;
                start = new Date(customStartDate + 'T00:00:00');
                end = new Date(customEndDate + 'T23:59:59.999');
                break;
            default:
                return null;
        }

        return {
            start: start.toISOString(),
            end: end.toISOString()
        };
    };

    const handleColumnToggle = (category: string, field: string) => {
        const columnKey = `${category}.${field}`;
        setSelectedColumns(prev =>
            prev.includes(columnKey)
                ? prev.filter(c => c !== columnKey)
                : [...prev, columnKey]
        );
    };

    const handleSelectAll = (category: string, fields: ColumnField[]) => {
        const categoryColumns = fields.map(f => `${category}.${f.field}`);
        const allSelected = categoryColumns.every(col => selectedColumns.includes(col));

        if (allSelected) {
            setSelectedColumns(prev => prev.filter(c => !categoryColumns.includes(c)));
        } else {
            setSelectedColumns(prev => [...new Set([...prev, ...categoryColumns])]);
        }
    };

    const handleExport = async () => {
        if (selectedColumns.length === 0) {
            showError('Please select at least one column to export');
            return;
        }

        if (selectedDateColumn && dateRangeType === 'custom' && (!customStartDate || !customEndDate)) {
            showError('Please select both start and end dates');
            return;
        }

        setExporting(true);
        try {
            const mergedFilters: any = {
                ...(asset_type && asset_type !== 'all' && { asset_type }),
                ...filters
            };

            if (selectedEventStatus) {
                mergedFilters.event_status = selectedEventStatus;
            } else if (templateName === 'events') {
                delete mergedFilters.event_status;
            }

            const payload: any = {
                format,
                timezone,
                columns: selectedColumns,
                ...(clientId && { client_id: clientId }),
                ...(Object.keys(mergedFilters).length > 0 && { filters: mergedFilters })
            };

            if (selectedDateColumn && dateRangeType) {
                if (dateRangeType === 'custom') {
                    const dateRange = getDateRange();
                    if (dateRange) {
                        payload.date_filter = {
                            column: selectedDateColumn,
                            operator: 'custom',
                            value: dateRange
                        };
                    }
                } else {
                    payload.date_filter = {
                        column: selectedDateColumn,
                        operator: dateRangeType,
                    };
                }
            }

            const response = await baseapi.post(
                `/export/${templateName}`,
                payload, { responseType: 'blob' }

            );

            if (response.data instanceof Blob) {
                const contentType = response.headers['content-type'];
                if (contentType && contentType.includes('application/json')) {
                    const text = await response.data.text();
                    const jsonData = JSON.parse(text);
                    if (jsonData.status === false) {
                        showError(jsonData.message || 'Export failed');
                        return;
                    }
                } else {
                    const url = window.URL.createObjectURL(response.data);
                    const a = document.createElement('a');
                    a.href = url;
                    const now = new Date();
                    const pad = (n: number) => String(n).padStart(2, '0');
                    const timestamp = `${now.getFullYear()}_${pad(now.getMonth() + 1)}_${pad(now.getDate())}_${pad(now.getHours())}_${pad(now.getMinutes())}`;
                    a.download = `${templateName}_export_${timestamp}.${format === 'csv' ? 'csv' : 'xlsx'}`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                    showSuccess('Export started successfully');
                    onClose();
                }
            }
        } catch (error: any) {
            console.error('Error exporting:', error);
            showError('Export failed. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    const dateTimeColumns = getDateTimeColumns();

    return (
        <Modal
            open={isOpen}
            className="flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
        >
            <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl animate-in fade-in zoom-in duration-300 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-[#422568] rounded-lg">
                            <Download className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-slate-800 tracking-tight">Export Data</h2>
                            <p className="text-[8px] text-slate-500 font-bold uppercase tracking-wider">Configure for <span className="text-[#422568]">{templateName}</span></p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-slate-50 rounded-lg transition-all group"
                    >
                        <X className="w-4 h-4 text-slate-400 group-hover:text-slate-800" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
                    {/* Left Panel: Settings */}
                    <div className="w-full md:w-56 border-r border-slate-100 p-4 space-y-4 overflow-y-auto custom-scroll bg-slate-50/50">
                        {/* Format Selection */}
                        <div className="space-y-4">
                            <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">File Format</label>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={() => setFormat('csv')}
                                    className={`flex flex-col items-center justify-center gap-1 p-2.5 rounded-lg border transition-all ${format === 'csv'
                                        ? 'bg-white border-[#422568] text-[#422568] shadow-md shadow-[#422568]/30'
                                        : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
                                        }`}
                                >
                                    <FileText className={`w-3.5 h-3.5 ${format === 'csv' ? 'text-[#422568]' : 'text-slate-300'}`} />
                                    <span className="text-[9px] font-black">CSV</span>
                                </button>
                                <button
                                    onClick={() => setFormat('excel')}
                                    className={`flex flex-col items-center justify-center gap-1 p-2.5 rounded-lg border transition-all ${format === 'excel'
                                        ? 'bg-white border-[#422568] text-[#422568] shadow-md shadow-[#422568]/30'
                                        : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
                                        }`}
                                >
                                    <FileSpreadsheet className={`w-3.5 h-3.5 ${format === 'excel' ? 'text-[#422568]' : 'text-slate-300'}`} />
                                    <span className="text-[9px] font-black">Excel</span>
                                </button>
                            </div>
                        </div>

                        {/* Date Filter */}
                        {dateTimeColumns.length > 0 && (
                            <div className="space-y-4">
                                <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                                    <Calendar className="w-3.5 h-3.5" />
                                    Date Range Filter
                                </label>

                                <div className="space-y-3">
                                    <div className="relative">
                                        <button
                                            onClick={() => {
                                                setIsDateColumnOpen(!isDateColumnOpen);
                                                setIsDateFilterOpen(false);
                                            }}
                                            className="w-full flex items-center justify-between p-2 bg-white border border-slate-200 rounded-lg text-[11px] text-slate-600 hover:border-slate-300 transition-all font-bold shadow-sm"
                                        >
                                            <span className="truncate">
                                                {selectedDateColumn ?
                                                    dateTimeColumns.find(c => `${c.category}.${c.field}` === selectedDateColumn)?.label :
                                                    'Select Date Column'
                                                }
                                            </span>
                                            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isDateColumnOpen ? 'rotate-180' : ''}`} />
                                        </button>

                                        {isDateColumnOpen && (
                                            <div className="absolute z-20 w-full bottom-full mb-1.5 bg-white border border-slate-200 rounded-lg shadow-2xl max-h-40 overflow-y-auto animate-in fade-in slide-in-from-bottom-1 font-bold">
                                                <div
                                                    className="px-4 py-2.5 text-xs text-slate-400 hover:bg-slate-50 cursor-pointer"
                                                    onClick={() => { setSelectedDateColumn(''); setIsDateColumnOpen(false); }}
                                                >
                                                    None
                                                </div>
                                                {dateTimeColumns.map((col) => {
                                                    const val = `${col.category}.${col.field}`;
                                                    return (
                                                        <div
                                                            key={val}
                                                            onClick={() => { setSelectedDateColumn(val); setIsDateColumnOpen(false); }}
                                                            className="flex items-center justify-between px-4 py-2.5 text-xs text-slate-600 hover:bg-[#422568] hover:text-white cursor-pointer"
                                                        >
                                                            {col.label}
                                                            {selectedDateColumn === val && <Check className="w-4 h-4" />}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>

                                    {selectedDateColumn && (
                                        <div className="relative">
                                            <button
                                                onClick={() => {
                                                    setIsDateFilterOpen(!isDateFilterOpen);
                                                    setIsDateColumnOpen(false);
                                                }}
                                                className="w-full flex items-center justify-between p-2 bg-white border border-slate-200 rounded-lg text-[11px] text-slate-600 hover:border-slate-300 transition-all font-bold shadow-sm"
                                            >
                                                <span>
                                                    {(() => {
                                                        switch (dateRangeType) {
                                                            case 'last_7_days': return 'Last 7 days';
                                                            case 'last_30_days': return 'Last 30 days';
                                                            case 'custom': return 'Custom Range';
                                                            default: return 'Select Range';
                                                        }
                                                    })()}
                                                </span>
                                                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isDateFilterOpen ? 'rotate-180' : ''}`} />
                                            </button>

                                            {isDateFilterOpen && (
                                                <div className="absolute z-20 w-full bottom-full mb-1.5 bg-white border border-slate-200 rounded-lg shadow-2xl animate-in fade-in slide-in-from-bottom-1 font-bold">
                                                    {[
                                                        { id: '', name: 'Clear' },
                                                        { id: 'last_7_days', name: 'Last 7 days' },
                                                        { id: 'last_30_days', name: 'Last 30 days' },
                                                        { id: 'custom', name: 'Custom Range' }
                                                    ].map((range) => (
                                                        <div
                                                            key={range.id}
                                                            onClick={() => { setDateRangeType(range.id); setIsDateFilterOpen(false); }}
                                                            className="flex items-center justify-between px-4 py-2.5 text-xs text-slate-600 hover:bg-[#422568] hover:text-white cursor-pointer"
                                                        >
                                                            {range.name}
                                                            {dateRangeType === range.id && <Check className="w-4 h-4" />}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                     {selectedDateColumn && dateRangeType === 'custom' && (
                                         <div className="grid grid-cols-1 gap-3 pt-2">
                                             <div className="space-y-1">
                                                 <label className="text-[10px] font-black text-slate-400 uppercase px-1">Start Date</label>
                                                 <div className="relative">
                                                     <input
                                                         type="date"
                                                         value={customStartDate}
                                                         onChange={(e) => setCustomStartDate(e.target.value)}
                                                         onClick={(e) => (e.target as HTMLInputElement).showPicker?.()}
                                                         className="w-full p-2 pr-9 bg-white border border-slate-200 rounded-lg text-[11px] text-slate-800 focus:border-[#422568] outline-none shadow-sm font-bold cursor-pointer"
                                                     />
                                                     <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                                                 </div>
                                             </div>
                                             <div className="space-y-1">
                                                 <label className="text-[10px] font-black text-slate-400 uppercase px-1">End Date</label>
                                                 <div className="relative">
                                                     <input
                                                         type="date"
                                                         value={customEndDate}
                                                         onChange={(e) => setCustomEndDate(e.target.value)}
                                                         onClick={(e) => (e.target as HTMLInputElement).showPicker?.()}
                                                         className="w-full p-2 pr-9 bg-white border border-slate-200 rounded-lg text-[11px] text-slate-800 focus:border-[#422568] outline-none shadow-sm font-bold cursor-pointer"
                                                     />
                                                     <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                                                 </div>
                                             </div>
                                         </div>
                                     )}
                                </div>
                            </div>
                        )}

                        {/* Status Filter */}
                        {templateName === 'events' && (
                            <div className="space-y-4 pt-2">
                                <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                                    <Activity className="w-3.5 h-3.5" />
                                    Event Status Filter
                                </label>
                                <div className="relative">
                                    <button
                                        onClick={() => {
                                            setIsStatusFilterOpen(!isStatusFilterOpen);
                                            setIsDateColumnOpen(false);
                                            setIsDateFilterOpen(false);
                                        }}
                                        className="w-full flex items-center justify-between p-2 bg-white border border-slate-200 rounded-lg text-[11px] text-slate-600 hover:border-slate-300 transition-all font-bold shadow-sm"
                                    >
                                        <span>
                                            {selectedEventStatus === 'open' ? 'Open' : selectedEventStatus === 'closed' ? 'Closed' : 'All Statuses'}
                                        </span>
                                        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isStatusFilterOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    {isStatusFilterOpen && (
                                        <div className="absolute z-20 w-full bottom-full mb-1.5 bg-white border border-slate-200 rounded-lg shadow-2xl animate-in fade-in slide-in-from-bottom-1 font-bold">
                                            {[
                                                { id: '', name: 'All Statuses' },
                                                { id: 'open', name: 'Open' },
                                                { id: 'closed', name: 'Closed' }
                                            ].map((statusOption) => (
                                                <div
                                                    key={statusOption.id}
                                                    onClick={() => { setSelectedEventStatus(statusOption.id); setIsStatusFilterOpen(false); }}
                                                    className="flex items-center justify-between px-4 py-2.5 text-xs text-slate-600 hover:bg-[#422568] hover:text-white cursor-pointer"
                                                >
                                                    {statusOption.name}
                                                    {selectedEventStatus === statusOption.id && <Check className="w-4 h-4" />}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Panel: Column Selection */}
                    <div className="flex-1 p-4 flex flex-col min-h-0 bg-white">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                            <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
                                <Columns className="w-3 h-3" />
                                Select Columns
                            </label>

                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={columnSearch}
                                    onChange={(e) => setColumnSearch(e.target.value)}
                                    className="w-full sm:w-40 bg-slate-50 border border-slate-100 rounded-lg pl-8 pr-2 py-1 text-[10px] text-slate-800 focus:border-[#422568] focus:bg-white outline-none transition-all font-bold placeholder:text-slate-400"
                                />
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto custom-scroll space-y-6 pr-2">
                            {loading ? (
                                <div className="flex flex-col items-center justify-center h-full gap-4 opacity-50">
                                    <div className="w-10 h-10 border-4 border-[#422568] border-t-[#422568] rounded-full animate-spin"></div>
                                    <span className="text-xs font-black text-slate-400 uppercase tracking-widest">Metadata Loading...</span>
                                </div>
                            ) : columns ? (
                                Object.entries(columns).map(([category, fields]) => {
                                    const filteredFields = fields.filter((f: ColumnField) =>
                                        f.label.toLowerCase().includes(columnSearch.toLowerCase()) ||
                                        f.field.toLowerCase().includes(columnSearch.toLowerCase())
                                    );

                                    if (filteredFields.length === 0) return null;

                                    const categoryColumns = fields.map((f: ColumnField) => `${category}.${f.field}`);
                                    const allSelected = categoryColumns.every((col: string) => selectedColumns.includes(col));
                                    const someSelected = categoryColumns.some((col: string) => selectedColumns.includes(col)) && !allSelected;

                                    return (
                                        <div key={category} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                                            {/* Category Header */}
                                            <div className="bg-slate-50/50 px-3 py-1.5 border-b border-slate-100 flex items-center justify-between">
                                                <label className="flex items-center gap-4 cursor-pointer group">
                                                    <div
                                                        onClick={() => handleSelectAll(category, fields)}
                                                        className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${allSelected
                                                            ? 'bg-[#422568] border-[#422568] shadow-sm shadow-[#422568]'
                                                            : someSelected
                                                                ? 'bg-[#422568]/40 border-[#422568]/40'
                                                                : 'bg-white border-slate-200 group-hover:border-slate-400'
                                                            }`}
                                                    >
                                                        {allSelected && <Check className="w-3 h-3 text-white" />}
                                                        {someSelected && <div className="w-2 h-0.5 bg-white rounded-full" />}
                                                    </div>
                                                    <span className="text-[9px] font-black uppercase text-slate-500 group-hover:text-slate-800 transition-colors tracking-wide">{category}</span>
                                                </label>
                                                <span className="text-[10px] font-black text-slate-400 bg-white px-2 py-1 rounded-lg border border-slate-100">
                                                    {categoryColumns.filter((c: string) => selectedColumns.includes(c)).length} / {fields.length}
                                                </span>
                                            </div>

                                            {/* Fields Grid */}
                                            <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                                                {filteredFields.map((field: ColumnField) => {
                                                    const columnKey = `${category}.${field.field}`;
                                                    const isSelected = selectedColumns.includes(columnKey);

                                                    return (
                                                        <label
                                                            key={field.field}
                                                            className="flex items-center gap-2.5 py-1 group cursor-pointer"
                                                        >
                                                            <div
                                                                onClick={() => handleColumnToggle(category, field.field)}
                                                                className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center transition-all ${isSelected
                                                                    ? 'bg-[#422568] border-[#422568] shadow-sm shadow-[#422568]'
                                                                    : 'bg-white border-slate-200 group-hover:border-slate-400'
                                                                    }`}
                                                            >
                                                                {isSelected && <Check className="w-3 h-3 text-white" />}
                                                            </div>
                                                            <span className={`text-[11px] transition-colors font-bold ${isSelected ? 'text-slate-800' : 'text-slate-400 group-hover:text-slate-600'}`}>
                                                                {field.label}
                                                            </span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="flex flex-col items-center justify-center h-full opacity-20 gap-4">
                                    <Columns className="w-16 h-16 text-slate-300" />
                                    <span className="text-[11px] font-black uppercase tracking-widest text-slate-400">No columns available</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/30 flex items-center justify-between">
                    <div className="flex flex-col">
                        <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none mb-0.5">Selection</span>
                        <div className="flex items-baseline gap-1">
                            <span className="text-lg font-black text-slate-800">{selectedColumns.length}</span>
                            <span className="text-[9px] text-slate-500 font-bold uppercase">Cols</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={onClose}
                            className="px-3 py-1.5 text-[9px] font-black text-slate-400 hover:text-slate-800 transition-colors uppercase tracking-widest"
                        >
                            Dismiss
                        </button>
                        <button
                            onClick={handleExport}
                            disabled={exporting || selectedColumns.length === 0}
                            className="flex items-center gap-1.5 px-5 py-2 bg-[#422568] hover:bg-[#422568] disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-white text-[9px] font-black rounded-lg transition-all shadow-lg active:scale-95"
                        >
                            {exporting ? (
                                <>
                                    <div className="w-2.5 h-2.5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                    ...
                                </>
                            ) : (
                                <>
                                    <Download className="w-3.5 h-3.5" />
                                    Generate
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Progress Bar for Exporting */}
                {exporting && (
                    <div className="h-1.5 w-full bg-slate-100 overflow-hidden relative">
                        <div className="h-full bg-[#422568] animate-progress origin-left"></div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default AgentExportModal;
